"use client";

import { Clock, MapPin, Navigation, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StudioLocation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const metadata = [{
    label: "STUDIO TYPE",
    value: "PRIVATE ATELIER"
  }, {
    label: "PARKING",
    value: "VALET AVAILABLE"
  }, {
    label: "VISITS",
    value: "BY APPOINTMENT"
  }];
  return <section data-section-id="2824" id="studio-location" className="relative bg-background text-foreground overflow-hidden dark" style={{
    minHeight: "80vh"
  }}>
      {}
      <div ref={ref} className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/studio_map_view.webp)]" aria-hidden="true" />

      {}
      <div className="absolute inset-0" style={{
      background: "radial-gradient(ellipse at center, transparent 40%, hsla(30, 10%, 8%, 0.85) 100%)",
      filter: "grayscale(100%) contrast(1.1)"
    }} aria-hidden="true" />
      <div className="absolute inset-0" style={{
      background: "linear-gradient(180deg, hsla(30, 10%, 8%, 0.35) 0%, transparent 30%, transparent 70%, hsla(30, 10%, 8%, 0.55) 100%)"
    }} aria-hidden="true" />

      {}
      <div className="absolute pointer-events-none" style={{
      top: "48%",
      left: "58%"
    }} aria-hidden="true">
        <span className="absolute inline-block size-3 -translate-x-1/2 -translate-y-1/2" style={{
        backgroundColor: "var(--primary)",
        boxShadow: "0 0 20px var(--primary), 0 0 40px var(--primary)"
      }} />
        <span className="absolute inline-block size-3 -translate-x-1/2 -translate-y-1/2 animate-ping" style={{
        backgroundColor: "var(--primary)",
        opacity: 0.6,
        animationDuration: "2s"
      }} />
        <span className="absolute inline-block size-8 -translate-x-1/2 -translate-y-1/2" style={{
        border: "1px solid var(--primary)",
        opacity: 0.4,
        animation: "pulse-ring 2s ease-out infinite"
      }} />
      </div>

      <style>{`
        @keyframes pulse-ring {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.8; }
          100% { transform: translate(-50%, -50%) scale(3); opacity: 0; }
        }
      `}</style>

      {}
      <div className={`absolute top-6 right-6 md:top-10 md:right-10 z-10 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}>
        <div className="flex items-center gap-2 px-4 py-2 backdrop-blur-xl border" style={{
        backgroundColor: "hsla(30, 10%, 8%, 0.8)",
        borderColor: "hsla(25, 20%, 85%, 0.15)",
        boxShadow: "0 8px 32px hsla(0, 0%, 0%, 0.6), 0 2px 8px hsla(0, 0%, 0%, 0.4)"
      }}>
          <Clock className="size-3.5" style={{
          color: "var(--primary)"
        }} />
          <span className="text-xs font-mono uppercase tracking-widest" style={{
          color: "var(--foreground)"
        }}>
            10:00 - 18:00
          </span>
          <span className="text-xs font-mono uppercase tracking-widest" style={{
          color: "var(--primary)"
        }}>
            · OPEN
          </span>
        </div>
      </div>

      {}
      <div className="relative" style={{
      minHeight: "80vh"
    }}>
        {}
        <div className={`absolute bottom-6 left-6 md:bottom-10 md:left-10 z-10 w-[calc(100%-3rem)] md:w-[480px] max-w-[480px] transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="backdrop-blur-xl border p-6 md:p-10" style={{
          backgroundColor: "hsla(30, 10%, 8%, 0.8)",
          borderColor: "hsla(25, 20%, 85%, 0.15)",
          boxShadow: "0 40px 80px -20px hsla(0, 0%, 0%, 0.8), 0 20px 40px -10px hsla(0, 0%, 0%, 0.6), 0 8px 16px hsla(0, 0%, 0%, 0.4)"
        }}>
            {}
            <div className="mb-6">
              <span className="text-xs font-mono uppercase tracking-widest" style={{
              color: "var(--primary)"
            }}>
                STUDIO — 02 · THE ATELIER
              </span>
            </div>

            {}
            <h2 className="font-default font-bold mb-6 leading-[1.05]" style={{
            fontSize: "clamp(1.875rem, 4vw, 2.375rem)",
            color: "var(--foreground)",
            letterSpacing: "-0.04em"
          }}>
              Joanne Glow,
              <br />
              Studio.
            </h2>

            {}
            <div className="h-px w-full mb-6" style={{
            backgroundColor: "var(--primary)",
            opacity: 0.7
          }} />

            {}
            <address className="not-italic mb-8">
              <p className="font-serif leading-relaxed" style={{
              fontSize: "17px",
              color: "hsl(40 12% 94%)"
            }}>
                128 Beauty Lane
                <br />
                Artisan District, City Center
                <br />
                United Kingdom
              </p>
            </address>

            {}
            <div className="mb-8">
              {metadata.map((item, i) => <div key={i} data-index={i} className="flex items-center justify-between py-3" style={{
              borderTop: i === 0 ? "1px solid hsla(25, 20%, 85%, 0.12)" : "none",
              borderBottom: "1px solid hsla(25, 20%, 85%, 0.12)"
            }}>
                  <span className="text-[10px] font-mono uppercase tracking-widest" style={{
                color: "var(--muted-foreground)"
              }}>
                    {item.label}
                  </span>
                  <span className="text-[11px] font-mono uppercase tracking-widest" style={{
                color: "var(--foreground)"
              }}>
                    · {item.value}
                  </span>
                </div>)}
            </div>

            {}
            <div className="grid grid-cols-2 gap-3">
              <Button className="font-mono uppercase tracking-widest text-xs h-12" style={{
              backgroundColor: "var(--primary)",
              color: "var(--primary-foreground)",
              boxShadow: "0 8px 24px hsla(25, 40%, 65%, 0.25)"
            }}>
                <Navigation className="size-3.5" />
                Get Directions
              </Button>
              <Button variant="outline" className="font-mono uppercase tracking-widest text-xs h-12 bg-transparent" style={{
              borderColor: "hsla(25, 20%, 85%, 0.25)",
              color: "var(--foreground)"
            }}>
                <Phone className="size-3.5" />
                Call Studio
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="absolute top-6 left-6 md:top-10 md:left-10 z-10 hidden md:flex items-center gap-2">
          <MapPin className="size-3.5" style={{
          color: "var(--primary)"
        }} />
          <span className="text-[10px] font-mono uppercase tracking-widest" style={{
          color: "var(--muted-foreground)"
        }}>
            51.5089° N · 0.1428° W
          </span>
        </div>
      </div>
    </section>;
}